import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import InputField from './InputField';
import SelectField from './SelectField';
import CheckBoxField from './CheckBoxField';
import FileField from './FileField';
import RadioButtonField from './RadioButtonField';
import TextAreaField from './TextAreaField';
import EmailField from './EmailField';
import FirstNameField from './FirstNameField';
import LastNameField from './LastNameField';
import PhoneField from './PhoneField';

function MultipleField({ 
  id, 
  elementNumber = 2, 
  nestedFields = [], 
  updateFieldData, 
  className,
  hasFirstNameField = false,
  hasLastNameField = false,
  hasEmailField = false,
  hasPhoneField = false
}) {
  const [fields, setFields] = useState(nestedFields);
  const [activeField, setActiveField] = useState(null);

  const fieldNameMap = {
    "InputField": InputField,
    "EmailField": EmailField,
    "FirstNameField": FirstNameField,
    "LastNameField": LastNameField,
    "PhoneField": PhoneField,
    "FileField": FileField,
    "CheckBoxField": CheckBoxField,
    "SelectField": SelectField,
    "RadioButtonField": RadioButtonField,
    "TextAreaField": TextAreaField,
  };

  const onNestedFieldDataChange = (nestedFieldId, data) => {
    const newFields = fields.map(field => {
      if (field.id === nestedFieldId) {
        return { ...field, ...data };
      }
      return field;
    });
    setFields(newFields);
    updateFieldData(id, { nestedFields: newFields });
  };

  const addNewField = (fieldType) => {
    const randomId = Math.floor(Math.random() * 1000);
    let field;

    switch (fieldType) {
      case 'input':
        field = {
          field: "InputField",
          type: "text",
          name: `input_${randomId}`,
          label: "Input",
          hasRange: false,
          minValue: 0,
          maxValue: 0,
          isRequired: false,
          id: uuidv4(),
        };
        break;
      case 'email':
        field = {
          field: "EmailField",
          type: "email",
          label: "Email",
          name: 'email',
          id: uuidv4(),
        };
        break;
      case 'firstname':
        field = {
          field: "FirstNameField",
          type: "text",
          name: "first_name",
          label: "First Name",
          id: uuidv4(),
        };
        break;
      case 'lastname':
        field = {
          field: "LastNameField",
          type: "text",
          name: "last_name",
          label: "Last Name",
          id: uuidv4(),
        };
        break;
      case 'phone':
        field = {
          field: "PhoneField",
          type: "tel",
          language: 'en',
          name: "phone",
          label: "Phone",
          isRequired: false,
          id: uuidv4(),
        };
        break;
      case 'file':
        field = {
          field: "FileField",
          type: "file",
          name: `file_${randomId}`,
          label: "File",
          isRequired: false,
          fileExtensions: [],
          id: uuidv4(),
        };
        break;
      case 'checkbox':
        field = {
          field: "CheckBoxField",
          type: "checkbox",
          name: `checkbox_${randomId}`,
          label: "Checkbox",
          isRequired: false,
          id: uuidv4(),
        };
        break;
      case 'select':
        field = {
          field: "SelectField",
          type: "select",
          name: `select_${randomId}`,
          label: "Select",
          isRequired: false,
          allowMultipleValue: false,
          options: [{ id: uuidv4(), value: "option 1" }],
          id: uuidv4(),
        };
        break;
      case 'radio':
        field = {
          field: "RadioButtonField",
          type: "radio",
          name: `radio_${randomId}`,
          label: "Radio",
          isRequired: false,
          choices: [{ id: uuidv4(), value: "choice 1" }],
          id: uuidv4(),
        };
        break;
      case 'textarea':
        field = {
          field: "TextAreaField",
          type: "textarea",
          name: `textarea_${randomId}`,
          label: "Textarea",
          isRequired: false,
          id: uuidv4(),
        };
        break;
      default:
        return;
    }

    const newFields = [...fields, field];
    setFields(newFields);
    updateFieldData(id, { nestedFields: newFields });
  };

  const removeField = (fieldId) => {
    const newFields = fields.filter(field => field.id !== fieldId);
    setFields(newFields);
    updateFieldData(id, { nestedFields: newFields });
  };

  const moveField = (fieldId, direction) => {
    const fieldIndex = fields.findIndex((field) => field.id === fieldId);
    if (
      (direction === "up" && fieldIndex === 0) ||
      (direction === "down" && fieldIndex === fields.length - 1)
    ) {
      return;
    }

    const newFields = [...fields];
    const movedField = newFields.splice(fieldIndex, 1)[0];
    const newIndex = direction === "up" ? fieldIndex - 1 : fieldIndex + 1;
    newFields.splice(newIndex, 0, movedField);
    setFields(newFields);
    updateFieldData(id, { nestedFields: newFields });
  };

 
  return (
    <div className={className}>
      <div className="flex flex-col space-y-6">
        

        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fields.map((field, index) => {
              const Field = fieldNameMap[field.field];
              return (
                <div 
                  key={field.id} 
                  className="relative border border-gray-200 rounded-md p-3 hover:shadow-sm transition-shadow"
                  onMouseEnter={() => setActiveField(field.id)}
                  onMouseLeave={() => setActiveField(null)}
                >
                  {activeField === field.id && (
                    <div className="absolute top-0 right-0 flex bg-white p-1 rounded-bl-md border-b border-l border-gray-200">
                      {index !== 0 && (
                        <button 
                          type="button"
                          onClick={() => moveField(field.id, "up")}
                          className="p-1 text-gray-500 hover:text-gray-700"
                          title="Move Up"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                      )}
                      
                      {index !== fields.length - 1 && (
                        <button 
                          type="button"
                          onClick={() => moveField(field.id, "down")}
                          className="p-1 text-gray-500 hover:text-gray-700"
                          title="Move Down"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                      
                      <button 
                        type="button"
                        onClick={() => removeField(field.id)}
                        className="p-1 text-red-500 hover:text-red-700"
                        title="Remove Field"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  
                  <div className="py-1 px-2 bg-gray-100 text-gray-600 text-xs mb-2 rounded-sm font-medium inline-block">
                    {field.field.replace('Field', '')}
                  </div>
                  
                  <Field
                    {...field}
                    updateFieldData={onNestedFieldDataChange}
                    className="bg-white"
                  />
                </div>
              );
            })}
          </div>
        </div>

        {fields.length < elementNumber && (
          <div>
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-2 bg-white text-sm text-gray-500">Add a field to this row</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {!hasEmailField && (
                <button
                  type="button"
                  onClick={() => addNewField('email')}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
                >
                  Email Field
                </button>
              )}
              
              {!hasPhoneField && (
                <button
                  type="button"
                  onClick={() => addNewField('phone')}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
                >
                  Phone Field
                </button>
              )}
              
              {!hasFirstNameField && (
                <button
                  type="button"
                  onClick={() => addNewField('firstname')}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
                >
                  First Name Field
                </button>
              )}
              
              {!hasLastNameField && (
                <button
                  type="button"
                  onClick={() => addNewField('lastname')}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
                >
                  Last Name Field
                </button>
              )}
              
              <button
                type="button"
                onClick={() => addNewField('input')}
                className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
              >
                Input Field
              </button>
              
              <button
                type="button"
                onClick={() => addNewField('select')}
                className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
              >
                Select Field
              </button>
              
              <button
                type="button"
                onClick={() => addNewField('checkbox')}
                className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
              >
                Checkbox Field
              </button>
              
              <button
                type="button"
                onClick={() => addNewField('radio')}
                className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
              >
                Radio Field
              </button>
              
              <button
                type="button"
                onClick={() => addNewField('textarea')}
                className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
              >
                Textarea Field
              </button>
              
              <button
                type="button"
                onClick={() => addNewField('file')}
                className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
              >
                File Field
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MultipleField;